import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Settings, Link } from 'lucide-react';
import config from '../config';
import { getCosClassName, getCpaClassName, useComponentMetrics } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';

import { calculateVariation, renderVariation as renderVariationBase } from '../utils/variationUtils';
import { bornesDeLaPeriodeChoisie, calculatePreviousPeriod as bornesDeComparaison } from '../utils/periodUtils';
/**
 * Composant table modulaire des termes de recherche combinés (Search + Shopping + Performance Max)
 * Optimisé avec React.memo et useMemo pour les performances
 */
const CombinedSearchTermsTable = React.memo(({
  autoCharts,
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  performanceMaxData,
  campaignFilter = 'all',
  cpcTierFilter = null,
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  openSearchTermModal,
  userRole = 'USER',
  compareEnabled = false,
  comparisonType = '',
  token
}) => {
  // Module ID pour la configuration des métriques
  const moduleId = 'search_terms_combined';

  // États locaux pour cette table
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const [previousSearchTermsData, setPreviousSearchTermsData] = useState([]);

  // Configuration des métriques pour ce composant
  const componentMetrics = useComponentMetrics('search_terms_combined', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // Calcul de la période précédente
  const calculatePreviousPeriod = useCallback(() => {
    if (!comparisonType || !selectedPeriod) return null;

    // Les bornes de la période regardée, puis celles auxquelles on la compare. Les deux
    // calculs vivaient ici, recopiés dans une vingtaine d'écrans : ils sont dans
    // `utils/periodUtils`, seul endroit à savoir ce que « hier » veut dire un jour de
    // changement d'heure.
    const plage = bornesDeLaPeriodeChoisie(selectedPeriod, customStartDate, customEndDate);
    if (!plage) return null;

    return bornesDeComparaison(plage.startDate, plage.endDate, comparisonType);
  }, [comparisonType, selectedPeriod, customStartDate, customEndDate]);


  // Affichage de la variation
  const renderVariation = (variation, isInversed = false) => (compareEnabled ? renderVariationBase(variation, isInversed) : null);

  // Extraction et traitement des données combinées (memoized)
  const combinedSearchTermsData = useMemo(() => {
    // Toujours afficher la section si on a un compte sélectionné
    if (!selectedAccount) {
      console.log(`❌ [COMBINED SECTION] Pas de compte sélectionné - section cachée`);
      return [];
    }

    // Vérifier que autoCharts existe
    if (!autoCharts) {
      console.log(`⏳ [COMBINED SECTION] autoCharts pas encore chargé`);
      return [];
    }

    // Récupérer les données Search/Shopping du backend
    const searchTermsBackendData = (autoCharts && autoCharts.rawData && selectedAccount) ? 
      autoCharts.rawData[selectedAccount.name] : null;

    let searchShoppingTerms = [];
    if (searchTermsBackendData) {
      console.log(`🔗 [COMBINED] searchTermsBackendData structure:`, Object.keys(searchTermsBackendData));
      
      // Nouvelles données backend - Format searchTermsData.terms
      if (searchTermsBackendData.searchTermsData && searchTermsBackendData.searchTermsData.terms) {
        console.log(`🔗 [COMBINED] Using searchTermsData.terms format`);
        searchShoppingTerms = searchTermsBackendData.searchTermsData.terms;
        
        if (searchShoppingTerms.length > 0) {
          console.log(`🔗 [COMBINED] Premier terme searchTermsData:`, searchShoppingTerms[0]);
          console.log(`🔗 [COMBINED] Propriétés searchTermsData:`, Object.keys(searchShoppingTerms[0]));
          console.log(`🔗 [COMBINED] Campagne info searchTermsData:`, {
            campaign: searchShoppingTerms[0].campaign,
            campaignId: searchShoppingTerms[0].campaignId,
            campaignName: searchShoppingTerms[0].campaignName
          });
        }
      }
      // Format alternatif avec searchTerms.searchTerms
      else if (searchTermsBackendData.searchTerms && searchTermsBackendData.searchTerms.searchTerms) {
        console.log(`🔗 [COMBINED] Using searchTerms.searchTerms format`);
        searchShoppingTerms = searchTermsBackendData.searchTerms.searchTerms;
        
        if (searchShoppingTerms.length > 0) {
          console.log(`🔗 [COMBINED] Premier terme searchTerms.searchTerms:`, searchShoppingTerms[0]);
          console.log(`🔗 [COMBINED] Propriétés searchTerms.searchTerms:`, Object.keys(searchShoppingTerms[0]));
        }
      }
      // Format avec searchTerms direct (array)
      else if (searchTermsBackendData.searchTerms && Array.isArray(searchTermsBackendData.searchTerms)) {
        console.log(`🔗 [COMBINED] Using searchTerms array format`);
        searchShoppingTerms = searchTermsBackendData.searchTerms;
        
        if (searchShoppingTerms.length > 0) {
          console.log(`🔗 [COMBINED] Premier terme searchTerms array:`, searchShoppingTerms[0]);
          console.log(`🔗 [COMBINED] Propriétés searchTerms array:`, Object.keys(searchShoppingTerms[0]));
        }
      }
    }

    // Récupérer les données Performance Max
    let performanceMaxTerms = [];
    if (performanceMaxData && selectedAccount) {
      const performanceMaxAccountData = performanceMaxData[selectedAccount.id] || performanceMaxData[selectedAccount.name];
      if (performanceMaxAccountData && performanceMaxAccountData.searchTerms) {
        if (Array.isArray(performanceMaxAccountData.searchTerms)) {
          performanceMaxTerms = performanceMaxAccountData.searchTerms.map(term => ({
            searchTerm: term.searchTerm || term.term || 'Non spécifié',
            campaignId: term.campaignId || null, // Utiliser campaignId depuis la nouvelle structure backend
            campaignName: term.campaignName || 'N/A',
            impressions: term.impressions || 0,
            clicks: term.clicks || 0,
            cost: term.cost || 0,
            conversions: term.conversions || 0,
            conversionsValue: term.conversionsValue || 0,
            source: 'Performance Max'
          }));
        }
      }
    }

    console.log(`🔗 [COMBINED SECTION] Search/Shopping terms: ${searchShoppingTerms.length}`);
    console.log(`🔗 [COMBINED SECTION] Performance Max terms: ${performanceMaxTerms.length}`);
    console.log(`🔗 [COMBINED SECTION] searchTermsBackendData:`, searchTermsBackendData);
    console.log(`🔗 [COMBINED SECTION] performanceMaxData:`, performanceMaxData);

    // Combiner toutes les données en préservant les informations de campagne
    const allSearchTermsRaw = [
      ...searchShoppingTerms.map((term, index) => {
        if (index < 3) { // Log des 3 premiers termes pour debug
          console.log(`🔗 [COMBINED] Mapping terme Search/Shopping ${index}:`, {
            searchTerm: term.searchTerm,
            campaignId: term.campaignId,
            campaignName: term.campaignName,
            source: 'Search/Shopping'
          });
        }
        
        return {
          searchTerm: term.searchTerm || term.term || 'Non spécifié',
          impressions: term.impressions || 0,
          clicks: term.clicks || 0,
          cost: term.cost || 0,
          conversions: term.conversions || 0,
          conversionsValue: term.conversionsValue || 0,
          campaign: term.campaign || term.campaignName || 'Non spécifié',
          campaignId: term.campaignId || null,
          adGroup: term.adGroup || term.adGroupName || null,
          adGroupId: term.adGroupId || null,
          source: term.source || 'Search/Shopping'
        };
      }),
      ...performanceMaxTerms.map((term, index) => {
        if (index < 3) { // Log des 3 premiers termes Performance Max pour debug
          console.log(`🔗 [COMBINED] Mapping terme Performance Max ${index}:`, {
            searchTerm: term.searchTerm,
            campaignId: term.campaignId,
            campaignName: term.campaignName,
            source: 'Performance Max'
          });
        }
        return term;
      })
    ];

    console.log(`🔗 [COMBINED SECTION] Total termes combinés: ${allSearchTermsRaw.length} (Search/Shopping: ${searchShoppingTerms.length}, Performance Max: ${performanceMaxTerms.length})`);

    if (allSearchTermsRaw.length === 0) {
      console.log(`❌ [COMBINED SECTION] Aucun terme de recherche - section cachée`);
      return [];
    }

    // Appliquer le filtre par campagne avant l'agrégation
    const filteredSearchTermsRaw = campaignFilter === 'all' 
      ? allSearchTermsRaw 
      : allSearchTermsRaw.filter(term => {
          // Gérer les cas où campaignId pourrait être null ou 'N/A'
          const termCampaignId = term.campaignId?.toString() || null;
          const filterCampaignId = campaignFilter?.toString() || null;
          return termCampaignId === filterCampaignId;
        });

    console.log(`🎯 [COMBINED SECTION] Filtre campagne "${campaignFilter}" appliqué: ${filteredSearchTermsRaw.length} termes (sur ${allSearchTermsRaw.length} total)`);

    // Appliquer le filtre par tranche de CPC si actif
    let cpcFilteredTerms = filteredSearchTermsRaw;
    if (cpcTierFilter && cpcTierFilter.min !== undefined && cpcTierFilter.max !== undefined) {
      cpcFilteredTerms = filteredSearchTermsRaw.filter(term => {
        const clicks = Number(term.clicks) || 0;
        const cost = Number(term.cost) || 0;
        const cpc = clicks > 0 ? (cost / clicks) : 0;
        
        // Filtrer par tranche de CPC
        return cpc >= cpcTierFilter.min && cpc <= cpcTierFilter.max;
      });
      
      console.log(`🎯 [CPC-TIER-FILTER] Filtre CPC ${cpcTierFilter.min}€-${cpcTierFilter.max}€ appliqué: ${cpcFilteredTerms.length} termes (sur ${filteredSearchTermsRaw.length} après filtre campagne)`);
    }

    // Agrégation par terme de recherche commun avec normalisation
    const aggregatedTerms = {};
    
    cpcFilteredTerms.forEach((term) => {
      const rawSearchTerm = term.searchTerm || term.term;
      if (!rawSearchTerm) return;
      
      // Normaliser le terme pour l'agrégation (trim + lowercase pour comparaison)
      const normalizedTerm = rawSearchTerm.trim().toLowerCase();
      const displayTerm = rawSearchTerm.trim(); // Garder la casse originale pour l'affichage
      

      if (!aggregatedTerms[normalizedTerm]) {
        aggregatedTerms[normalizedTerm] = {
          searchTerm: displayTerm, // Utiliser le terme original pour l'affichage
          sources: [],
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversionsValue: 0
        };
      }

      const agg = aggregatedTerms[normalizedTerm];
      if (!agg.sources.includes(term.source)) {
        agg.sources.push(term.source);
      }

      agg.impressions += term.impressions || 0;
      agg.clicks += term.clicks || 0;
      agg.cost += term.cost || 0;
      agg.conversions += term.conversions || 0;
      agg.conversionsValue += term.conversionsValue || 0;
    });
    

    const combinedTermsArray = Object.values(aggregatedTerms);

    // Compter les sources
    const sourceCounts = {};
    combinedTermsArray.forEach(term => {
      term.sources.forEach(source => {
        sourceCounts[source] = (sourceCounts[source] || 0) + 1;
      });
    });
    console.log(`🔗 [COMBINED SECTION] Répartition des sources:`, sourceCounts);

    return combinedTermsArray;
  }, [autoCharts, selectedAccount, performanceMaxData, campaignFilter, cpcTierFilter]);

  // Traitement des données avec calculs de métriques (memoized)
  const processedCombinedTerms = useMemo(() => {
    if (!combinedSearchTermsData.length) return [];

    return combinedSearchTermsData.map(term => {
      // Parser les valeurs si elles sont en string avec €
      const impressions = typeof term.impressions === 'string' 
        ? parseInt(term.impressions.replace(/[^\d]/g, '')) || 0 
        : term.impressions || 0;
      const clicks = typeof term.clicks === 'string' 
        ? parseInt(term.clicks.replace(/[^\d]/g, '')) || 0 
        : term.clicks || 0;
      const cost = typeof term.cost === 'string' 
        ? parseFloat(term.cost.replace(/[€\s]/g, '').replace(',', '.')) || 0 
        : term.cost || 0;
      const conversions = typeof term.conversions === 'string' 
        ? parseFloat(term.conversions.replace(',', '.')) || 0 
        : term.conversions || 0;
      const conversionsValue = typeof term.conversionsValue === 'string' 
        ? parseFloat(term.conversionsValue.replace(/[€\s]/g, '').replace(',', '.')) || 0 
        : term.conversionsValue || 0;

      // Calculs des métriques dérivées
      const ctr = impressions > 0 ? (clicks / impressions * 100) : 0;
      const cpc = clicks > 0 ? (cost / clicks) : 0;
      const conversionRate = clicks > 0 ? (conversions / clicks * 100) : 0;
      const cpa = conversions > 0 ? (cost / conversions) : 0;
      const cos = conversionsValue > 0 ? (cost / conversionsValue * 100) : 0;

      return {
        ...term,
        impressions,
        clicks,
        cost,
        conversions,
        conversionsValue,
        ctr,
        cpc,
        conversionRate,
        cpa,
        cos
      };
    });
  }, [combinedSearchTermsData]);

  // Fonction de tri memoized
  const sortedCombinedTerms = useMemo(() => {
    if (!processedCombinedTerms.length) return [];

    const sorted = [...processedCombinedTerms];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      if (sortConfig.key === 'searchTerm') {
        // Pour les termes de recherche, tri alphabétique
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      // Gestion spéciale pour CPA (éviter les valeurs infinies)
      if (sortConfig.key === 'cpa') {
        // Les termes sans conversions (CPA infini) vont à la fin
        if (a.conversions === 0 && b.conversions === 0) return 0;
        if (a.conversions === 0) return 1;
        if (b.conversions === 0) return -1;
      }
      
      // Pour les valeurs numériques
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [processedCombinedTerms, sortConfig]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!processedCombinedTerms.length) return {};

    const baseTotals = processedCombinedTerms.reduce((acc, term) => ({
      impressions: acc.impressions + (term.impressions || 0),
      clicks: acc.clicks + (term.clicks || 0),
      cost: acc.cost + (term.cost || 0),
      conversions: acc.conversions + (term.conversions || 0),
      conversionsValue: acc.conversionsValue + (term.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    // Calculer les métriques dérivées
    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [processedCombinedTerms]);

  // Charger les données de la période précédente pour la comparaison
  useEffect(() => {
    const loadPreviousPeriodData = async () => {
      console.log('🔄 [COMBINED-SEARCH-TERMS] useEffect déclenché:', {
        compareEnabled,
        comparisonType,
        selectedAccount: selectedAccount?.id,
        hasToken: !!token
      });

      if (!compareEnabled || !comparisonType || !selectedAccount || !token) {
        console.log('⏭️ [COMBINED-SEARCH-TERMS] Conditions non remplies, skip chargement');
        setPreviousSearchTermsData([]);
        return;
      }

      const previousDates = calculatePreviousPeriod();
      if (!previousDates) {
        setPreviousSearchTermsData([]);
        return;
      }

      try {
        const requestBody = {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: previousDates.startDate,
          customEndDate: previousDates.endDate
        };

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/search-terms`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        console.log('📥 [COMBINED-SEARCH-TERMS] Réponse API brute:', response.data);

        if (response.data) {
          // La réponse a la structure: {success: true, searchTermsResults: {...}, pagination: {...}}
          // Les données sont dans searchTermsResults, pas directement dans response.data
          const resultsData = response.data.searchTermsResults || response.data;

          const accountKey = Object.keys(resultsData).find(key => !key.includes('_error') && key !== 'success' && key !== 'pagination');
          console.log('🔑 [COMBINED-SEARCH-TERMS] Account key trouvée:', accountKey);
          console.log('🔑 [COMBINED-SEARCH-TERMS] Clés disponibles dans resultsData:', Object.keys(resultsData));

          if (accountKey && resultsData[accountKey]) {
            const data = resultsData[accountKey];
            console.log('📦 [COMBINED-SEARCH-TERMS] Structure des données:', Object.keys(data));
            let terms = [];

            if (data.searchTerms && data.searchTerms.searchTerms) {
              terms = data.searchTerms.searchTerms;
            } else if (data.searchTerms && Array.isArray(data.searchTerms)) {
              terms = data.searchTerms;
            } else if (data.searchTermsData && data.searchTermsData.terms) {
              terms = data.searchTermsData.terms;
            }

            setPreviousSearchTermsData(terms);
            console.log('✅ [COMBINED-SEARCH-TERMS] Données période précédente chargées:', { terms: terms.length });
          } else {
            console.log('⚠️ [COMBINED-SEARCH-TERMS] Pas de données trouvées pour la clé');
            setPreviousSearchTermsData([]);
          }
        } else {
          console.log('⚠️ [COMBINED-SEARCH-TERMS] Réponse vide');
          setPreviousSearchTermsData([]);
        }
      } catch (err) {
        console.error('❌ [COMBINED-SEARCH-TERMS] Erreur chargement période précédente:', err);
        setPreviousSearchTermsData([]);
      }
    };

    loadPreviousPeriodData();
  }, [compareEnabled, comparisonType, selectedAccount, selectedPeriod, customStartDate, customEndDate, token]);

  // Map pour accéder rapidement aux données de la période précédente par terme de recherche
  const previousSearchTermsMap = useMemo(() => {
    if (!compareEnabled || !previousSearchTermsData.length) return {};
    const map = {};
    previousSearchTermsData.forEach(term => {
      const termText = (term.searchTerm || term.term || '').toLowerCase().trim();
      if (termText) {
        if (!map[termText]) {
          map[termText] = {
            impressions: 0,
            clicks: 0,
            cost: 0,
            conversions: 0,
            conversionsValue: 0
          };
        }
        map[termText].impressions += term.impressions || 0;
        map[termText].clicks += term.clicks || 0;
        map[termText].cost += term.cost || 0;
        map[termText].conversions += term.conversions || 0;
        map[termText].conversionsValue += term.conversionsValue || 0;
      }
    });
    // Calculer les métriques dérivées
    Object.values(map).forEach(term => {
      term.ctr = term.impressions > 0 ? (term.clicks / term.impressions * 100) : 0;
      term.cpc = term.clicks > 0 ? (term.cost / term.clicks) : 0;
      term.conversionRate = term.clicks > 0 ? (term.conversions / term.clicks * 100) : 0;
      term.cpa = term.conversions > 0 ? (term.cost / term.conversions) : 0;
      term.cos = term.conversionsValue > 0 ? (term.cost / term.conversionsValue * 100) : 0;
    });
    return map;
  }, [compareEnabled, previousSearchTermsData]);

  // Totaux de la période précédente
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousSearchTermsData.length) return null;

    const baseTotals = previousSearchTermsData.reduce((acc, term) => ({
      impressions: acc.impressions + (term.impressions || 0),
      clicks: acc.clicks + (term.clicks || 0),
      cost: acc.cost + (term.cost || 0),
      conversions: acc.conversions + (term.conversions || 0),
      conversionsValue: acc.conversionsValue + (term.conversionsValue || 0)
    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

    return {
      ...baseTotals,
      ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
      cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
      conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
      cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
      cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
    };
  }, [compareEnabled, previousSearchTermsData]);

  // Configuration de pagination
  const itemsPerPage = 20;
  const totalPages = Math.ceil(sortedCombinedTerms.length / itemsPerPage);

  // Données paginées memoized
  const paginatedCombinedTerms = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedCombinedTerms.slice(startIndex, endIndex);
  }, [sortedCombinedTerms, currentPage, itemsPerPage]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
    setCurrentPage(1); // Réinitialiser à la première page
  }, []);

  // Handler de pagination memoized
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  // Handler d'ouverture modal memoized
  const handleTermClick = useCallback((term) => {
    if (openSearchTermModal) {
      openSearchTermModal(term);
    }
  }, [openSearchTermModal]);


  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, sortedCombinedTerms.length);

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><Link size={16} />&nbsp;Termes de recherche Search + Shopping + Performance Max</h4>
        <div className="section-header-buttons">
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          <button
            onClick={onToggleVisibility}
            className="section-toggle-btn"
            title={isVisible ? "Masquer la section" : "Afficher la section"}
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>
      </div>

      {isVisible && (
        <div className="table-container">
          <div className="campaigns-table-wrapper">
            <table className="campaigns-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('searchTerm')} className="sortable">
                    Terme de recherche {sortConfig.key === 'searchTerm' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {isComponentMetricSelected('impressions') && (
                    <th onClick={() => handleSort('impressions')} className="sortable numeric">
                      Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('ctr') && (
                    <th onClick={() => handleSort('ctr')} className="sortable numeric">
                      CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('clics') && (
                    <th onClick={() => handleSort('clicks')} className="sortable numeric">
                      Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cpc') && (
                    <th onClick={() => handleSort('cpc')} className="sortable numeric">
                      CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cout') && (
                    <th onClick={() => handleSort('cost')} className="sortable numeric">
                      Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('taux_conv') && (
                    <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                      Taux conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('conv') && (
                    <th onClick={() => handleSort('conversions')} className="sortable numeric">
                      Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cpa') && (
                    <th onClick={() => handleSort('cpa')} className="sortable numeric">
                      CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('valeur_conv') && (
                    <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                      Valeur conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                  {isComponentMetricSelected('cos') && (
                    <th onClick={() => handleSort('cos')} className="sortable numeric">
                      COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {processedCombinedTerms.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <div>
                        <h4 style={{ margin: '0 0 10px 0', color: '#888', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><Link size={16} /> Aucun terme de recherche trouvé</h4>
                        <p style={{ margin: '0 0 15px 0', fontSize: '0.9em' }}>
                          Aucun terme de recherche avec des clics n'a été trouvé pour cette période.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedCombinedTerms.map((term, index) => {
                    const cos = term.cos || 0;
                    const termKey = (term.searchTerm || '').toLowerCase().trim();
                    const prevTerm = previousSearchTermsMap[termKey];

                    // Debug: Afficher les infos de comparaison pour les 3 premiers termes
                    if (index < 3) {
                      console.log(`🔍 [COMBINED-RENDER] Terme ${index}:`, {
                        searchTerm: term.searchTerm,
                        termKey,
                        compareEnabled,
                        hasPrevTerm: !!prevTerm,
                        prevTermData: prevTerm,
                        previousMapSize: Object.keys(previousSearchTermsMap).length
                      });
                    }

                    return (
                      <tr key={index}>
                        <td
                          onClick={() => handleTermClick(term)}
                          style={{
                            cursor: 'pointer',
                            maxWidth: '250px'
                          }}
                          title="Cliquer pour voir les détails"
                        >
                          <div>
                            <div style={{
                              color: '#1a73e8',
                              fontWeight: '500',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {term.searchTerm || 'Non spécifié'}
                            </div>
                            <div style={{ marginTop: '2px' }}>
                              {term.sources.map((source, idx) => (
                                <span
                                  key={idx}
                                  style={{
                                    display: 'inline-block',
                                    padding: '1px 4px',
                                    margin: '0 2px 0 0',
                                    borderRadius: '8px',
                                    fontSize: '0.65em',
                                    fontWeight: '600',
                                    textTransform: 'uppercase',
                                    backgroundColor: source === 'Performance Max' ? '#e3f2fd' : '#f3e5f5',
                                    color: source === 'Performance Max' ? '#1565c0' : '#7b1fa2'
                                  }}
                                >
                                  {source === 'Search/Shopping' ? 'Search' : source === 'Performance Max' ? 'PMax' : source}
                                </span>
                              ))}
                            </div>
                          </div>
                        </td>
                        {isComponentMetricSelected('impressions') && (
                          <td className="numeric">
                            {formatNumber(term.impressions || 0)}
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(term.impressions || 0, prevTerm.impressions || 0))}
                          </td>
                        )}
                        {isComponentMetricSelected('ctr') && (
                          <td className="numeric">
                            {formatNumber(term.ctr || 0, 2)}%
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(term.ctr || 0, prevTerm.ctr || 0))}
                          </td>
                        )}
                        {isComponentMetricSelected('clics') && (
                          <td className="numeric">
                            {formatNumber(term.clicks || 0)}
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(term.clicks || 0, prevTerm.clicks || 0))}
                          </td>
                        )}
                        {isComponentMetricSelected('cpc') && (
                          <td className="numeric">
                            €{formatNumber(term.cpc || 0, 2)}
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(term.cpc || 0, prevTerm.cpc || 0), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('cout') && (
                          <td className="numeric">
                            €{formatNumber(term.cost || 0, 2)}
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(term.cost || 0, prevTerm.cost || 0), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('taux_conv') && (
                          <td className="numeric">
                            {formatNumber(term.conversionRate || 0, 2)}%
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(term.conversionRate || 0, prevTerm.conversionRate || 0))}
                          </td>
                        )}
                        {isComponentMetricSelected('conv') && (
                          <td className="numeric">
                            {formatNumber(term.conversions || 0, 1)}
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(term.conversions || 0, prevTerm.conversions || 0))}
                          </td>
                        )}
                        {isComponentMetricSelected('cpa') && (
                          <td className={`numeric ${getCpaClassName(term.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                            {term.conversions > 0 ? `€${formatNumber(term.cpa, 2)}` : '∞'}
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(term.cpa, prevTerm.cpa), true)}
                          </td>
                        )}
                        {isComponentMetricSelected('valeur_conv') && (
                          <td className="numeric">
                            €{formatNumber(term.conversionsValue || 0, 2)}
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(term.conversionsValue || 0, prevTerm.conversionsValue || 0))}
                          </td>
                        )}
                        {isComponentMetricSelected('cos') && (
                          <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', moduleId)}`}>
                            {formatNumber(cos, 1)}%
                            {compareEnabled && prevTerm && renderVariation(calculateVariation(cos, prevTerm.cos || 0), true)}
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>

              {paginatedCombinedTerms.length > 0 && (
                <tfoot>
                  <tr className="total-row">
                    <td><strong>TOTAL</strong></td>
                    {isComponentMetricSelected('impressions') && (
                      <td className="numeric">
                        <strong>{formatNumber(totals.impressions)}</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
                      </td>
                    )}
                    {isComponentMetricSelected('ctr') && (
                      <td className="numeric">
                        <strong>{formatNumber(totals.ctr, 2)}%</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
                      </td>
                    )}
                    {isComponentMetricSelected('clics') && (
                      <td className="numeric">
                        <strong>{formatNumber(totals.clicks)}</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
                      </td>
                    )}
                    {isComponentMetricSelected('cpc') && (
                      <td className="numeric">
                        <strong>€{formatNumber(totals.cpc, 2)}</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
                      </td>
                    )}
                    {isComponentMetricSelected('cout') && (
                      <td className="numeric">
                        <strong>€{formatNumber(totals.cost, 2)}</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
                      </td>
                    )}
                    {isComponentMetricSelected('taux_conv') && (
                      <td className="numeric">
                        <strong>{formatNumber(totals.conversionRate, 2)}%</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
                      </td>
                    )}
                    {isComponentMetricSelected('conv') && (
                      <td className="numeric">
                        <strong>{formatNumber(totals.conversions, 1)}</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
                      </td>
                    )}
                    {isComponentMetricSelected('cpa') && (
                      <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId)}`}>
                        <strong>€{formatNumber(totals.cpa, 2)}</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
                      </td>
                    )}
                    {isComponentMetricSelected('valeur_conv') && (
                      <td className="numeric">
                        <strong>€{formatNumber(totals.conversionsValue, 2)}</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
                      </td>
                    )}
                    {isComponentMetricSelected('cos') && (
                      <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId)}`}>
                        <strong>{formatNumber(totals.cos, 1)}%</strong>
                        {compareEnabled && previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
                      </td>
                    )}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Contrôles de pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>
                  Affichage {startIndex + 1}-{endIndex} sur {sortedCombinedTerms.length} termes de recherche combinés
                </span>
              </div>
              <div className="pagination-buttons">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  ← Précédent
                </button>
                <span className="page-numbers">
                  Page {currentPage} sur {totalPages}
                </span>
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Suivant →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="search_terms_combined"
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Termes de recherche combinés"
      />
    </div>
  );
});

CombinedSearchTermsTable.displayName = 'CombinedSearchTermsTable';

export default CombinedSearchTermsTable;